import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, CalendarDays, Newspaper, BookOpen,
  Lock, GripVertical, X, ExternalLink, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getContentCalendar, rescheduleContent } from '../utils/api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const pad = (n) => String(n).padStart(2, '0');
const dateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Build the visible grid: full weeks (Mon-Sun) covering the month
const buildGrid = (cursor) => {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - ((first.getDay() + 6) % 7)); // back to Monday
  const days = [];
  const d = new Date(start);
  while (true) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
    if (d.getMonth() !== cursor.getMonth() && d.getDay() === 1 && days.length >= 28) break;
    if (days.length >= 42) break;
  }
  return days;
};

const typeStyles = {
  news: {
    chip: 'bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-200',
    live: 'bg-slate-100 text-slate-500 border-l-4 border-l-teal-400 border-slate-200',
    badge: 'bg-teal-100 text-teal-700',
    icon: Newspaper,
    label: 'News',
  },
  blog: {
    chip: 'bg-violet-100 text-violet-800 border-violet-200 hover:bg-violet-200',
    live: 'bg-slate-100 text-slate-500 border-l-4 border-l-violet-400 border-slate-200',
    badge: 'bg-violet-100 text-violet-700',
    icon: BookOpen,
    label: 'Blog',
  },
};

const AdminContentCalendarPage = () => {
  const today = new Date();
  const todayKey = dateKey(today);
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // item detail modal
  const [dayModal, setDayModal] = useState(null); // "+N more" day list
  const [dragOver, setDragOver] = useState(null);
  const [moveDate, setMoveDate] = useState('');

  const grid = buildGrid(cursor);
  const gridStart = dateKey(grid[0]);
  const gridEnd = dateKey(grid[grid.length - 1]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getContentCalendar(gridStart, gridEnd);
      setItems(data.items);
    } catch (e) {
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [gridStart, gridEnd]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const byDay = items.reduce((acc, it) => {
    (acc[it.sydney_date] = acc[it.sydney_date] || []).push(it);
    return acc;
  }, {});

  const monthItems = items.filter((it) => it.sydney_date.slice(0, 7) === `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`);
  const scheduledCount = monthItems.filter((it) => !it.is_live).length;
  const liveCount = monthItems.filter((it) => it.is_live).length;

  const doReschedule = async (item, newDate) => {
    if (item.is_live) { toast.error('That story is already live — it can\'t be moved'); return; }
    if (newDate <= todayKey) { toast.error('Pick a future day — it publishes at 12pm Sydney'); return; }
    if (newDate === item.sydney_date) return;
    const prev = items;
    setItems(items.map((it) => (it.id === item.id && it.type === item.type ? { ...it, sydney_date: newDate } : it)));
    try {
      const res = await rescheduleContent(item.type, item.id, newDate);
      setItems((cur) => cur.map((it) => (it.id === item.id && it.type === item.type ? { ...it, sydney_date: res.sydney_date, published_at: res.published_at } : it)));
      toast.success(`"${item.title.slice(0, 40)}${item.title.length > 40 ? '…' : ''}" moved to ${newDate}`);
    } catch (e) {
      setItems(prev);
      toast.error(e.response?.data?.detail || 'Failed to reschedule');
    }
  };

  const onDrop = (e, day) => {
    e.preventDefault();
    setDragOver(null);
    try {
      const item = JSON.parse(e.dataTransfer.getData('text/plain'));
      doReschedule(item, dateKey(day));
    } catch { /* ignore */ }
  };

  const openItem = (item) => { setSelected(item); setMoveDate(''); setDayModal(null); };

  const Chip = ({ item, full = false }) => {
    const s = typeStyles[item.type];
    const Icon = s.icon;
    return (
      <button
        data-testid={`calendar-item-${item.type}-${item.id}`}
        draggable={!item.is_live}
        onDragStart={(e) => e.dataTransfer.setData('text/plain', JSON.stringify(item))}
        onClick={() => openItem(item)}
        title={item.title}
        className={`w-full flex items-center gap-1 px-1.5 py-1 rounded-md border text-left text-[11px] leading-tight font-medium transition-colors ${item.is_live ? s.live : `${s.chip} cursor-grab active:cursor-grabbing`}`}
      >
        {item.is_live ? <Lock className="h-3 w-3 flex-shrink-0" /> : <GripVertical className="h-3 w-3 flex-shrink-0 opacity-50" />}
        <Icon className="h-3 w-3 flex-shrink-0" />
        <span className={full ? '' : 'truncate'}>{item.title}</span>
      </button>
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6" data-testid="content-calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-1 sm:mb-2 flex items-center gap-3">
            <CalendarDays className="h-7 w-7 sm:h-9 sm:w-9 text-primary" />
            Content Calendar
          </h1>
          <p className="text-sm sm:text-base text-slate-600">
            Everything scheduled to publish, at a glance. Drag a story to another day to reschedule it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/news"><Button variant="outline" size="sm">News Crawler</Button></Link>
          <Link to="/admin/blog"><Button variant="outline" size="sm">Blog Manager</Button></Link>
        </div>
      </div>

      {/* Month nav + legend */}
      <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev-month" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-2 hover:bg-slate-100 rounded-lg"><ChevronLeft className="h-5 w-5" /></button>
          <h2 className="text-lg font-bold text-slate-900 w-44 text-center" data-testid="calendar-month-label">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h2>
          <button data-testid="calendar-next-month" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-2 hover:bg-slate-100 rounded-lg"><ChevronRight className="h-5 w-5" /></button>
          <Button data-testid="calendar-today-btn" variant="outline" size="sm" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}>Today</Button>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-teal-100 border border-teal-300" /> News</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-violet-100 border border-violet-300" /> Blog</span>
          <span className="flex items-center gap-1.5"><Lock className="h-3 w-3" /> Already live (locked)</span>
          <span className="font-semibold text-slate-900" data-testid="calendar-month-stats">{scheduledCount} scheduled · {liveCount} live this month</span>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[760px]" data-testid="calendar-grid">
            <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
              {WEEKDAYS.map((w) => (
                <div key={w} className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide text-center">{w}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {grid.map((day) => {
                const key = dateKey(day);
                const dayItems = byDay[key] || [];
                const inMonth = day.getMonth() === cursor.getMonth();
                const isToday = key === todayKey;
                const isPast = key < todayKey;
                const droppable = !isPast && !isToday;
                return (
                  <div
                    key={key}
                    data-testid={`calendar-day-${key}`}
                    onDragOver={(e) => { if (droppable) { e.preventDefault(); setDragOver(key); } }}
                    onDragLeave={() => setDragOver((cur) => (cur === key ? null : cur))}
                    onDrop={(e) => droppable && onDrop(e, day)}
                    className={`min-h-[104px] border-b border-r border-slate-100 p-1.5 flex flex-col gap-1 transition-colors
                      ${inMonth ? 'bg-white' : 'bg-slate-50/60'}
                      ${dragOver === key ? 'bg-primary/10 ring-2 ring-inset ring-primary' : ''}
                      ${isPast ? 'opacity-70' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full
                        ${isToday ? 'bg-primary text-white' : inMonth ? 'text-slate-700' : 'text-slate-400'}`}>
                        {day.getDate()}
                      </span>
                      {dayItems.length > 0 && (
                        <span className="text-[10px] font-bold text-slate-400">{dayItems.length}</span>
                      )}
                    </div>
                    {dayItems.slice(0, 3).map((item) => (
                      <Chip key={`${item.type}-${item.id}`} item={item} />
                    ))}
                    {dayItems.length > 3 && (
                      <button
                        data-testid={`calendar-more-${key}`}
                        onClick={() => setDayModal({ key, items: dayItems })}
                        className="text-[11px] font-semibold text-primary hover:underline text-left px-1"
                      >
                        +{dayItems.length - 3} more
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {loading && <div className="p-3 text-center text-sm text-slate-400">Loading…</div>}
      </div>

      {/* Info bar */}
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs sm:text-sm text-blue-800">
        <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <p>Stories publish at <strong>12pm Sydney time</strong> on their scheduled day, and members receive them as <strong>one combined daily email</strong>. Locked items are already live. On mobile, tap a story and use "Move to" instead of dragging.</p>
      </div>

      {/* Day "+N more" modal */}
      {dayModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDayModal(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md max-h-[70vh] overflow-y-auto space-y-2" onClick={(e) => e.stopPropagation()} data-testid="calendar-day-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-slate-900">{dayModal.key} — {dayModal.items.length} items</h3>
              <button onClick={() => setDayModal(null)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="h-5 w-5" /></button>
            </div>
            {dayModal.items.map((item) => (
              <Chip key={`${item.type}-${item.id}`} item={item} full />
            ))}
          </div>
        </div>
      )}

      {/* Item detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()} data-testid="calendar-item-modal">
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${typeStyles[selected.type].badge}`}>
                  {React.createElement(typeStyles[selected.type].icon, { className: 'h-3 w-3' })}
                  {typeStyles[selected.type].label}
                  {selected.is_live ? ' · Live' : ' · Scheduled'}
                </span>
                <h3 className="font-bold text-slate-900 mt-2 leading-snug">{selected.title}</h3>
                {selected.source_name && <p className="text-xs text-slate-500 mt-1">Source: {selected.source_name}</p>}
              </div>
              <button onClick={() => setSelected(null)} className="p-1 hover:bg-slate-100 rounded-lg flex-shrink-0"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-slate-600">
              {selected.is_live ? 'Went live' : 'Publishes'} on <strong>{selected.sydney_date}</strong> at 12pm Sydney time.
            </p>
            <Link
              to={selected.type === 'blog' ? `/blog/${selected.slug}` : `/news/${selected.slug}`}
              className="inline-flex items-center gap-1 text-sm text-primary font-semibold hover:underline"
            >
              <ExternalLink className="h-4 w-4" /> View {typeStyles[selected.type].label.toLowerCase()} post
            </Link>
            {!selected.is_live && (
              <div className="border-t border-slate-100 pt-4">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Move to another day</label>
                <div className="flex gap-2 mt-2">
                  <input
                    data-testid="reschedule-date-input"
                    type="date"
                    value={moveDate}
                    min={dateKey(new Date(today.getTime() + 86400000))}
                    onChange={(e) => setMoveDate(e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                  <Button
                    data-testid="reschedule-btn"
                    size="sm"
                    disabled={!moveDate}
                    onClick={async () => { await doReschedule(selected, moveDate); setSelected(null); }}
                  >
                    Move
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContentCalendarPage;
